import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthProvider';
import { useProfile, useHousehold } from '../../hooks/useApi';
import { Currency, Country } from '../../types/api.types';
import styles from './ProfilePage.module.scss';

const CURRENCIES: { value: Currency; label: string }[] = [
    { value: 'USD', label: 'USD ($)' },
    { value: 'INR', label: 'INR (₹)' },
    { value: 'CAD', label: 'CAD (C$)' },
];

const COUNTRY_LABELS: Record<Country, string> = {
    US: 'United States',
    INDIA: 'India',
    CAN: 'Canada',
};

function formatDate(iso: string): string {
    return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

function isEmail(value: string): boolean {
    return value.includes('@');
}

export function ProfilePage() {
    const { user } = useAuth();
    const { profile, loading: profileLoading, error: profileError, updateProfile } = useProfile();
    const { household, loading: householdLoading } = useHousehold();

    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);

    const [displayName, setDisplayName] = useState('');
    const [birthYear, setBirthYear] = useState('');
    const [targetRetirementAge, setTargetRetirementAge] = useState('');
    const [baseCurrency, setBaseCurrency] = useState<Currency | ''>('');

    const startEditing = () => {
        if (!profile) return;
        const storedName = profile.displayName;
        setDisplayName(isEmail(storedName) ? (user?.firstName ?? '') : storedName);
        setBirthYear(String(profile.birthYear ?? ''));
        setTargetRetirementAge(String(profile.targetRetirementAge));
        setBaseCurrency(profile.baseCurrency);
        setSaveError(null);
        setEditing(true);
    };

    const cancelEditing = () => {
        setEditing(false);
        setSaveError(null);
    };

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        setSaveError(null);
        try {
            await updateProfile({
                displayName,
                birthYear: Number(birthYear),
                targetRetirementAge: Number(targetRetirementAge),
                baseCurrency: baseCurrency as Currency,
            });
            setEditing(false);
        } catch (err) {
            setSaveError(err instanceof Error ? err.message : 'Failed to save');
        } finally {
            setSaving(false);
        }
    };

    if (profileLoading || householdLoading) {
        return (
            <div className={styles.page}>
                <p className={styles.loadingText}>Loading profile...</p>
            </div>
        );
    }

    if (profileError) {
        return (
            <div className={styles.page}>
                <p className={styles.errorText}>{profileError}</p>
            </div>
        );
    }

    const resolvedName =
        profile?.displayName && !isEmail(profile.displayName) ? profile.displayName : user?.firstName || '';
    const avatarLetter = (resolvedName || user?.email || '?')[0].toUpperCase();
    const yearsToRetirement = profile ? profile.targetRetirementAge - profile.currentAge : null;
    const uniqueCountries = profile ? [...new Set(profile.residencyHistory.map((r) => r.country))] : [];
    const residencyCount = profile?.residencyHistory?.length ?? 0;

    return (
        <div className={styles.page}>
            {/* Hero */}
            <div className={styles.hero}>
                <div>
                    <h1 className={styles.heroTitle}>My Profile</h1>
                    <p className={styles.heroSubtitle}>
                        Manage your residency status, household details, and core financial parameters.
                    </p>
                </div>
                {!editing && (
                    <button className={styles.editGlobalBtn} onClick={startEditing}>
                        <span className={styles.btnIcon}>✏</span>
                        Edit Profile
                    </button>
                )}
            </div>

            {/* Identity card */}
            <section className={styles.identityCard}>
                <div className={styles.avatarWrap}>
                    <div className={styles.avatarRing}>
                        <div className={styles.avatarCircle}>{avatarLetter}</div>
                    </div>
                    <div className={styles.statusDot} />
                </div>

                <div className={styles.identityInfo}>
                    <h2 className={styles.identityName}>{resolvedName || 'User'}</h2>
                    <p className={styles.identityEmail}>{user?.email}</p>
                    <div className={styles.badges}>
                        {uniqueCountries.length > 1 && <span className={styles.badgePrimary}>Multi-Tax Resident</span>}
                        {household?.membership?.role && (
                            <span className={styles.badgeSecondary}>
                                {household.membership.role === 'OWNER' ? 'Household Owner' : 'Member'}
                            </span>
                        )}
                    </div>
                </div>

                {(household?.metadata?.name || profile?.createdAt) && (
                    <div className={styles.identityMeta}>
                        {household?.metadata?.name && (
                            <div className={styles.metaItem}>
                                <p className={styles.metaLabel}>Household</p>
                                <p className={styles.metaValue}>{household.metadata.name}</p>
                            </div>
                        )}
                        {profile?.createdAt && (
                            <div className={styles.metaItem}>
                                <p className={styles.metaLabel}>Member Since</p>
                                <p className={styles.metaValue}>{formatDate(profile.createdAt)}</p>
                            </div>
                        )}
                    </div>
                )}
            </section>

            {/* Financial Parameters */}
            <section className={styles.section}>
                <div className={styles.sectionHeaderRow}>
                    <h3 className={styles.sectionTitle}>Financial Parameters</h3>
                </div>

                {editing ? (
                    <form className={styles.form} onSubmit={handleSave}>
                        <div className={styles.formGrid}>
                            <div className={styles.field}>
                                <label className={styles.fieldLabel}>Display Name</label>
                                <input
                                    type="text"
                                    className={styles.input}
                                    value={displayName}
                                    onChange={(e) => setDisplayName(e.target.value)}
                                    required
                                />
                            </div>
                            <div className={styles.field}>
                                <label className={styles.fieldLabel}>Birth Year</label>
                                <input
                                    type="number"
                                    className={styles.input}
                                    value={birthYear}
                                    onChange={(e) => setBirthYear(e.target.value)}
                                    min={1940}
                                    max={new Date().getFullYear() - 18}
                                    required
                                />
                            </div>
                            <div className={styles.field}>
                                <label className={styles.fieldLabel}>Target Retirement Age</label>
                                <input
                                    type="number"
                                    className={styles.input}
                                    value={targetRetirementAge}
                                    onChange={(e) => setTargetRetirementAge(e.target.value)}
                                    min={40}
                                    max={75}
                                    required
                                />
                            </div>
                            <div className={styles.field}>
                                <label className={styles.fieldLabel}>Base Currency</label>
                                <select
                                    className={styles.input}
                                    value={baseCurrency}
                                    onChange={(e) => setBaseCurrency(e.target.value as Currency)}
                                    required
                                >
                                    <option value="">Select currency</option>
                                    {CURRENCIES.map((c) => (
                                        <option key={c.value} value={c.value}>
                                            {c.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        {saveError && <p className={styles.formError}>{saveError}</p>}
                        <div className={styles.formActions}>
                            <button type="button" className={styles.cancelBtn} onClick={cancelEditing}>
                                Cancel
                            </button>
                            <button type="submit" className={styles.saveBtn} disabled={saving}>
                                {saving ? 'Saving...' : 'Save Changes'}
                            </button>
                        </div>
                    </form>
                ) : (
                    <div className={styles.dataTiles}>
                        <div className={`${styles.tile} ${styles.tileBlue}`}>
                            <p className={styles.tileLabel}>Birth Year</p>
                            <p className={styles.tileValue}>{profile?.birthYear || '—'}</p>
                        </div>
                        <div className={`${styles.tile} ${styles.tileTeal}`}>
                            <p className={styles.tileLabel}>Current Age</p>
                            <p className={styles.tileValue}>{profile?.currentAge ?? '—'}</p>
                        </div>
                        <div className={`${styles.tile} ${styles.tileAmber}`}>
                            <p className={styles.tileLabel}>Retirement Target</p>
                            <p className={styles.tileValue}>{profile?.targetRetirementAge ?? '—'}</p>
                        </div>
                        <div className={`${styles.tile} ${styles.tileRed}`}>
                            <p className={styles.tileLabel}>Base Currency</p>
                            <p className={styles.tileValue}>{profile?.baseCurrency || '—'}</p>
                        </div>
                        <div className={`${styles.tile} ${styles.tileAccent}`}>
                            <p className={`${styles.tileLabel} ${styles.tileLabelOnAccent}`}>Years to Go</p>
                            <p className={`${styles.tileValue} ${styles.tileValueOnAccent}`}>
                                {yearsToRetirement !== null && yearsToRetirement > 0 ? yearsToRetirement : '—'}
                            </p>
                        </div>
                    </div>
                )}
            </section>

            {/* Bottom two-column grid */}
            <div className={styles.bottomGrid}>
                {/* Residency Timeline */}
                <section className={styles.section}>
                    <div className={styles.sectionHeaderRow}>
                        <h3 className={styles.sectionTitle}>Residency Timeline</h3>
                        <Link to="/residency" className={styles.manageLink}>
                            ⚙ Manage Residency
                        </Link>
                    </div>

                    {residencyCount === 0 ? (
                        <div className={styles.emptyCard}>
                            <p className={styles.emptyText}>No residency periods defined.</p>
                            <Link to="/residency" className={styles.addPeriodBtn}>
                                + Add your first period
                            </Link>
                        </div>
                    ) : (
                        <div className={styles.timelineCard}>
                            <p className={styles.timelineCount}>
                                {residencyCount} residency period{residencyCount !== 1 ? 's' : ''} defined for
                                cross-border tax calculation.
                            </p>
                            <div className={styles.timelineList}>
                                {profile!.residencyHistory.map((entry, i) => {
                                    const isLast = i === profile!.residencyHistory.length - 1;
                                    return (
                                        <div key={i} className={styles.timelineRow}>
                                            <div className={styles.timelineDotCol}>
                                                <div
                                                    className={`${styles.timelineDot} ${i === 0 ? styles.dotActive : styles.dotInactive}`}
                                                />
                                                {!isLast && <div className={styles.timelineLine} />}
                                            </div>
                                            <div className={styles.timelineBody}>
                                                <div className={styles.timelineTop}>
                                                    <div>
                                                        <p className={styles.timelineCountry}>
                                                            {COUNTRY_LABELS[entry.country] ?? entry.country}
                                                        </p>
                                                        <p className={styles.timelineAge}>
                                                            Age {entry.startAge} – {entry.endAge}
                                                        </p>
                                                    </div>
                                                    {i === 0 && <span className={styles.activeTag}>Active</span>}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </section>

                {/* Household */}
                <section className={styles.section}>
                    <h3 className={styles.sectionTitle}>Household</h3>
                    {household?.metadata?.name ? (
                        <div className={styles.householdCard}>
                            <div className={styles.householdIconWrap}>👥</div>
                            <h4 className={styles.householdName}>{household.metadata.name}</h4>
                            {household.membership?.role && (
                                <p className={styles.householdRole}>
                                    {household.membership.role === 'OWNER' ? 'Owner' : 'Member'}
                                </p>
                            )}
                            {household.membership?.joinedAt && (
                                <p className={styles.householdJoined}>
                                    Joined {formatDate(household.membership.joinedAt)}
                                </p>
                            )}
                        </div>
                    ) : (
                        <div className={styles.householdEmptyCard}>
                            <div className={styles.householdEmptyIcon}>👥</div>
                            <h4 className={styles.householdEmptyTitle}>No household linked</h4>
                            <p className={styles.householdEmptyText}>
                                Add family members to enable shared cross-border asset planning.
                            </p>
                        </div>
                    )}
                </section>
            </div>
        </div>
    );
}
